import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError, AxiosResponse } from "axios";
import { includes } from "lodash";
import { ApiResources } from "@/api/resources";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import useAxiosPrivate from "../../../api/hooks/useAxiosPrivate";
import { ALERT_STATUS } from "../../../context/alert/types";
import {
  convertStringBasedOnLength,
  DRIVERS_ACTIONS,
  RESOURCE_ACTIONS,
  ROUTES,
  TABLE_NAMES
} from "../../../shared";
import {
  capitalizeResourceFirstLetter,
  refreshTableAndUpdateDetails,
  removeSlashFromPath
} from "../../../shared/functions/functions";
import useRefreshTableData from "../../../shared/hooks/useRefreshTableData";
import useResponse from "../../../shared/hooks/useResponse";
import useTableItemDetails from "../../DetailsSection/hooks/useTableItemDetails";
import useCompanies from "../../Navbar/components/CompanySelector/hooks/useCompanies";
import { Actions } from "../../PageHeader/actions/types";
import { deselectAllRows } from "../functions";
import { RefreshPageContentParams, VEHICLE_ACTIVATION_STATUS } from "../types";

const useActionsForSelection = (route: ROUTES) => {
  const { deleteData, bulkDeleteData, bulkRemoveData, bulkMoveData, postData } =
    useAxiosPrivate();
  const { handleResponse, handleCommonError } = useResponse();
  const { refreshTableData } = useRefreshTableData();
  const { getTableItemDetails } = useTableItemDetails();
  const { t } = useTranslation();

  const { getQueryData } = useCompanies();

  const {
    selectedRows,
    data: tableRows,
    detailsData,
    params: { contains, selectedTableItem },
    setSelectedRows,
    resetTableDetails
  } = useTableStoreByRoute(route);

  const filterNotDeletingItems = () => {
    const filteredItems = tableRows.filter(
      item => !selectedRows.includes(item.id as string)
    );
    return filteredItems;
  };

  const getSuccessActionString = (
    actionString: string,
    isDeleteItems: boolean,
    customCount?: number | string
  ) => {
    const resourceLength =
      customCount === undefined ? selectedRows.length : Number(customCount);

    return isDeleteItems
      ? actionString
      : convertStringBasedOnLength(resourceLength, actionString);
  };

  const getResourceId = (
    deleteSelectedItems: boolean,
    detailsDataItemId: any
  ) => {
    const notSelectedRows = tableRows.filter(
      row => !selectedRows.includes(String(row.id))
    );

    const resourceIdForDelete =
      detailsData && notSelectedRows.length ? notSelectedRows[0].id : null;

    return deleteSelectedItems ? resourceIdForDelete : detailsDataItemId;
  };

  const refreshPageContent = ({
    resourceString,
    payloadValues,
    action,
    deleteSelectedItems = true,
    companyString,
    customSuccessMessage,
    useCustomCount
  }: RefreshPageContentParams) => {
    const customSuccessfulCount = useCustomCount ? resourceString : undefined;

    const successMessage = customSuccessMessage
      ? undefined
      : t(
          `Alert##successfully ${getSuccessActionString(
            action,
            deleteSelectedItems,
            customSuccessfulCount
          )}`,
          {
            resourceString,
            companyString
          }
        );

    const valueInSearchField = contains ?? "";
    const detailsDataItemId = detailsData?.id;

    if (selectedRows.includes(detailsDataItemId)) {
      if (filterNotDeletingItems().length > 0 || !successMessage) {
        const itemHasOpenedDetails =
          detailsDataItemId &&
          (Array.isArray(payloadValues)
            ? payloadValues.includes(detailsDataItemId)
            : payloadValues === detailsDataItemId);

        const resourceId = getResourceId(
          deleteSelectedItems,
          detailsDataItemId
        );

        refreshTableAndUpdateDetails({
          valueInSearchField,
          resourceId,
          successMessage,
          itemHasOpenedDetails,
          getTableItemDetails,
          handleResponse,
          leaveRowsChecked: false
        });
      } else {
        refreshTableData(undefined, valueInSearchField);
      }
    } else {
      refreshTableData(undefined, valueInSearchField);

      deselectAllRows(setSelectedRows);
    }
    successMessage && handleResponse(ALERT_STATUS.Success, successMessage);

    customSuccessMessage &&
      handleResponse(
        customSuccessMessage.status,
        customSuccessMessage.title,
        customSuccessMessage.description
      );
  };

  const deleteSingleItem = async (
    resource: ROUTES,
    setLoading: Dispatch<SetStateAction<boolean>>
  ) => {
    try {
      setLoading(true);
      await deleteData(resource, selectedRows[0]);

      const resourceString =
        resource === ROUTES.IntegrationsApi
          ? t("General##api credential")
          : capitalizeResourceFirstLetter(resource).slice(0, -1);

      refreshPageContent({
        resourceString,
        payloadValues: selectedRows[0],
        action: RESOURCE_ACTIONS.Deleted
      });
    } catch (error) {
      const err = error as AxiosError;
      if (err) {
        const errorMessage = err.message;
        handleResponse(ALERT_STATUS.Critical, errorMessage);
        setLoading(false);
      }
    }
    setLoading(false);
  };

  const generateBulkDeleteResourceString = (
    resource: TABLE_NAMES | ROUTES,
    numberOfItems?: number
  ) => {
    const isSingle = numberOfItems === 1;

    switch (resource) {
      case ROUTES.Companies:
        return isSingle
          ? t("General##company")
          : `${numberOfItems} ${removeSlashFromPath(resource)}`;

      case ROUTES.IntegrationsApi:
        return isSingle
          ? t("General##api credential")
          : t("General##api credentials");

      case ROUTES.AnalysisProviders:
        return isSingle
          ? t("General##integration")
          : t("General##integrations");

      default:
        return capitalizeResourceFirstLetter(resource);
    }
  };

  const deleteBulkOfItems = async (
    resource: TABLE_NAMES | ROUTES,
    payload: object | null,
    setLoading: Dispatch<SetStateAction<boolean>>,
    numberOfItems?: number
  ) => {
    try {
      setLoading(true);
      await bulkDeleteData(resource, payload);
      const payloadValues = Object.values(payload!)[0];

      const matchedOpenedItem = includes(payloadValues, selectedTableItem);

      if (matchedOpenedItem) {
        resetTableDetails();
      }

      refreshPageContent({
        resourceString: generateBulkDeleteResourceString(
          resource,
          numberOfItems
        ),
        payloadValues,
        action: RESOURCE_ACTIONS.Deleted
      });

      if (resource === ROUTES.Companies) {
        await getQueryData(true);
      }
    } catch (error) {
      handleCommonError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const removeBulkOfItems = async (
    resource: TABLE_NAMES | string,
    payload: object | null,
    resourceAction: Actions,
    setLoading: Dispatch<SetStateAction<boolean>>,
    handleCloseDialog: () => void
  ) => {
    await applyBulkChangesToItems(
      resource,
      payload,
      resourceAction,
      setLoading,
      handleCloseDialog,
      bulkRemoveData
    );
  };

  const bulkApplyQuickSchedules = async (
    resource: TABLE_NAMES | string,
    payload: object | null,
    resourceAction: Actions,
    setLoading: Dispatch<SetStateAction<boolean>>,
    handleCloseDialog: () => void
  ) => {
    const driverNum = selectedRows.length;

    const alertTitle =
      driverNum === 1
        ? t("Alert##quick schedule created##title single driver")
        : t("Alert##quick schedule created##title multiple drivers", {
            driverNum
          });

    const customSuccessMessage = {
      status: ALERT_STATUS.Info,
      title: alertTitle,
      description: t("Alert##quick schedule created##description##drivers")
    };

    await applyBulkChangesToItems(
      resource,
      payload,
      resourceAction,
      setLoading,
      handleCloseDialog,
      postData,
      customSuccessMessage
    );
  };

  const applyBulkChangesToItems = async (
    resource: TABLE_NAMES | string,
    payload: object | null,
    resourceAction: Actions,
    setLoading: Dispatch<SetStateAction<boolean>>,
    handleCloseDialog: () => void,
    resourceFunction: (
      resource: string,
      payload: object | null
    ) => Promise<AxiosResponse<any, any, {}>>,
    customSuccessMessage?: {
      status: ALERT_STATUS;
      title: string;
      description?: string;
    }
  ) => {
    try {
      setLoading(true);

      const isDriverBulkQuickSchedule =
        resourceAction === DRIVERS_ACTIONS.BulkQuickScheduleDownload;

      await resourceFunction(resource, payload);

      const payloadValueNum = isDriverBulkQuickSchedule ? 0 : 1;

      const payloadValues = Object.values(payload!)[payloadValueNum];

      refreshPageContent({
        resourceString: selectedRows.length,
        payloadValues,
        action: resourceAction,
        deleteSelectedItems: false,
        customSuccessMessage
      });

      deselectAllRows(setSelectedRows);

      handleCloseDialog();
    } catch (error) {
      handleCommonError(error, t);
      setLoading(false);
    }
    setLoading(false);
  };

  const moveBulkItems = async (
    resource: TABLE_NAMES | string,
    payload: object | null,
    resourceAction: Actions,
    setLoading: Dispatch<SetStateAction<boolean>>,
    handleCloseDialog: () => void
  ) => {
    try {
      setLoading(true);

      const { company, ...rest } = payload as any;

      const convertedPayload: string[][] = {
        ...rest,
        companyId: company.id
      };

      await bulkMoveData(resource, convertedPayload);

      const payloadValues: string[] = Object.values(convertedPayload)[0];

      const resourceString =
        payloadValues.length > 1
          ? t(`Table##${resource}`)
          : t(`Table##${resource.slice(0, -1)}`);

      refreshPageContent({
        resourceString,
        payloadValues,
        action: resourceAction,
        deleteSelectedItems: false,
        companyString: company.name
      });

      deselectAllRows(setSelectedRows);

      handleCloseDialog();
    } catch (error) {
      handleCommonError(error, t);
    }
    setLoading(false);
  };

  const bulkActivateVehicles = async (
    payload: object | null,
    resourceAction: Actions,
    setLoading: Dispatch<SetStateAction<boolean>>,
    handleCloseDialog: () => void
  ) => {
    try {
      setLoading(true);

      const { data } = await postData(
        ApiResources.VehiclesBulkActivate,
        payload
      );

      const changedVehiclesCount = data?.items?.filter(
        (item: any) =>
          item.result === VEHICLE_ACTIVATION_STATUS.Activated ||
          item.result === VEHICLE_ACTIVATION_STATUS.Deactivated
      ).length;

      const payloadValues = Object.values(payload!)[1];

      refreshPageContent({
        resourceString: changedVehiclesCount,
        payloadValues,
        action: resourceAction,
        deleteSelectedItems: false,
        useCustomCount: true
      });

      deselectAllRows(setSelectedRows);

      handleCloseDialog();
    } catch (error) {
      handleCommonError(error, t);
      setLoading(false);
    }
    setLoading(false);
  };

  return {
    deleteSingleItem,
    deleteBulkOfItems,
    removeBulkOfItems,
    moveBulkItems,
    bulkApplyQuickSchedules,
    bulkActivateVehicles,
    applyBulkChangesToItems
  };
};

export default useActionsForSelection;
